import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { GraduationCap, Book, Languages as LanguagesIcon, X, MapPin } from 'lucide-react';
import { useAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import OnboardingTemplate from '../OnboardingTemplate';
import { EDUCATION_LEVELS, EXPERIENCE_LEVELS, LANGUAGES } from '@/data/constants';
import { ALL_COUNTRIES } from '@/data/countries';
import { getFlagEmoji } from '@/utils/countryFlags';
import { getYearsFromExperienceLevel } from '@/components/dashboard/profile/about-me/utils/experienceUtils';

interface ProfileStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ProfileStep: React.FC<ProfileStepProps> = ({ onNext, onPrevious }) => {
  const { user } = useAuth();
  const [educationValue, setEducationValue] = useState('');
  const [experienceLevel, setExperienceLevel] = useState('');

  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [countryValue, setCountryValue] = useState('');
  const [languagesValue, setLanguagesValue] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');


  // Update countryValue if profile.location changes (async load)
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (data) {
        setProfile(data);
        setCountryValue(data.location || '');
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user]);

  // Stepper/progress bar content
  const step = 1;
  const totalSteps = 3;
  const stepperContent = (
    <div className="mb-4">
      <div className="flex items-center mb-2">
        {[...Array(totalSteps)].map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 mx-1 rounded transition-colors duration-300 ${i === 0 ? 'bg-orange-500' : 'bg-gray-200'}`}
            style={{ maxWidth: 180 }}
          />
        ))}
      </div>
      <div className="text-xs text-muted-foreground mb-1">Step {step} of {totalSteps}</div>
    </div>
  );

  const availableLanguages = useMemo(() => {
    return LANGUAGES.filter(lang => !languagesValue.includes(lang.label));
  }, [languagesValue]);

  const handleAddLanguage = () => {
    if (selectedLanguage && !languagesValue.includes(selectedLanguage)) {
      setLanguagesValue([...languagesValue, selectedLanguage]);
      setSelectedLanguage('');
    }
  };
  const handleRemoveLanguage = (lang: string) => {
    setLanguagesValue(languagesValue.filter(l => l !== lang));
  };

  const handleNext = async () => {
    if (!user?.id) return;
    await supabase
      .from('profiles')
      .update({
        education: educationValue,
        years_experience: getYearsFromExperienceLevel(experienceLevel),
        location: countryValue,
        languages: languagesValue,
        onboarding_status: 'in_progress',
        onboarding_step: 2
      })
      .eq('id', user.id);
    onNext();
  };

  return (
    <OnboardingTemplate showStepper={true} stepperContent={stepperContent}>
      <h1 className="text-2xl font-bold mb-4">Education & Languages</h1>
      {/* Education */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-semibold">Education</span>
        </div>
        <Select value={educationValue} onValueChange={setEducationValue}>
          <SelectTrigger>
            <SelectValue placeholder="Select education" />
          </SelectTrigger>
          <SelectContent>
            {EDUCATION_LEVELS.map((option) => (
              <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Experience */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-semibold">Years of Experience</span>
        </div>
        <Select value={experienceLevel} onValueChange={setExperienceLevel}>
          <SelectTrigger>
            <SelectValue placeholder="Select experience" />
          </SelectTrigger>
          <SelectContent>
            {EXPERIENCE_LEVELS.map((option) => (
              <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Country */}
      
      {/* Languages */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-semibold">Languages</span>
        </div>
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {languagesValue.map((lang, idx) => (
              <Badge key={idx} className="px-3 py-1.5 flex items-center gap-1.5">
                {lang}
                <button onClick={() => handleRemoveLanguage(lang)} className="ml-1 text-white hover:text-gray-200">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {languagesValue.length === 0 && (
              <p className="text-sm text-muted-foreground">No languages added yet.</p>
            )}
          </div>
          <div className="flex gap-2">
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a language" />
              </SelectTrigger>
              <SelectContent>
                {availableLanguages.map((language) => (
                  <SelectItem key={language.id} value={language.label}>
                    {language.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAddLanguage} disabled={!selectedLanguage}>
              Add
            </Button>
          </div>
        </div>
      </div>
      <div className="flex flex-row gap-2 pt-2">
        <Button variant="outline" onClick={onPrevious} className="w-1/2">
          Back
        </Button>
        <Button onClick={handleNext} disabled={!educationValue || !experienceLevel || !countryValue} className="w-1/2">
          Next
        </Button>
      </div>
    </OnboardingTemplate>
  );
};

export default ProfileStep; 